import React, { useState, useEffect, useRef } from "react";
import { Card } from "antd";
import "./MessageCard.css";

// --- MOCK MODES ---
// Toggle these to independently mock network or memory warnings for demo/testing
const MOCK_NETWORK = false; // Set to true to always show 'bad' network warning
const MOCK_MEMORY = false;  // Set to true to always show 'bad' memory warning

// ---- SPEED TEST CONFIG ----
// TODO: Update these with your actual S3 file URL and size in bytes
const SPEED_TEST_URL = 'https://public-files-network.s3.ap-southeast-2.amazonaws.com/speedtest-1mb.bin'; // <-- UPDATE THIS
const SPEED_TEST_FILE_SIZE = 1900000; // <-- UPDATE THIS (bytes)
const SPEED_TEST_INTERVAL = 5 * 60 * 1000; // 5 minutes in ms
const DISMISS_TIMEOUT = 5 * 60 * 1000; // 5 minutes in ms

// Helper to format bytes (copied from ReportIssueButton.js)
const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper to run a real download speed test
async function testDownloadSpeed() {
  const controller = new AbortController();
  const timeout = setTimeout(() => {controller.abort()}, 20000); // 20 seconds
  const start = performance.now();
  try {
    await fetch(SPEED_TEST_URL, { cache: 'no-store', signal: controller.signal });
    const end = performance.now();
    clearTimeout(timeout);
    const durationSeconds = (end - start) / 1000;
    const speedMbps = (SPEED_TEST_FILE_SIZE * 8) / (durationSeconds * 1_000_000);
    return speedMbps;
  } catch (e) {
    clearTimeout(timeout);
    // Treat as bad if fetch fails or times out
    throw new Error('Speed test failed or timed out');
  }
}

export default function MessageCard() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [networkStatus, setNetworkStatus] = useState(null); // 'bad', 'moderate', 'ok', or null
  const [measuredSpeed, setMeasuredSpeed] = useState(null); // Mbps
  const [memoryStatus, setMemoryStatus] = useState(null); // 'bad', 'moderate', 'ok', or null
  const [memoryInfo, setMemoryInfo] = useState({});
  const [dismissedNetwork, setDismissedNetwork] = useState(false);
  const [dismissedMemory, setDismissedMemory] = useState(false);
  const networkTimerRef = useRef(null);
  const memoryTimerRef = useRef(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const speedTestIntervalRef = useRef(null);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // --- Real download speed test for network ---
  useEffect(() => {
    if (MOCK_NETWORK) {
      setNetworkStatus('bad');
      setMeasuredSpeed(null);
      return;
    }
    let cancelled = false;
    let isMounted = true;
    async function runSpeedTest() {
      try {
        const speed = await testDownloadSpeed();
        if (cancelled || !isMounted) return;
        setMeasuredSpeed(speed);
        if (speed < 5) setNetworkStatus('bad');
        else if (speed < 10) setNetworkStatus('moderate');
        else setNetworkStatus('ok');
      } catch (e) {
        if (!cancelled && isMounted) {
          setMeasuredSpeed(null);
          setNetworkStatus('bad');
        }
      }
    }
    runSpeedTest();
    speedTestIntervalRef.current = setInterval(runSpeedTest, SPEED_TEST_INTERVAL);
    return () => {
      cancelled = true;
      isMounted = false;
      if (speedTestIntervalRef.current) clearInterval(speedTestIntervalRef.current);
    };
  }, []);

  // --- Mock or Real Memory ---
  useEffect(() => {
    if (MOCK_MEMORY) {
      setMemoryStatus('bad');
      setMemoryInfo({
        deviceMemory: 1,
        jsHeapSizeLimit: 200 * 1024 * 1024,
        usedJSHeapSize: 180 * 1024 * 1024,
        totalJSHeapSize: 200 * 1024 * 1024,
      });
    } else {
      let memStatus = null;
      let memInfo = {};
      if (navigator.deviceMemory) {
        memInfo.deviceMemory = navigator.deviceMemory;
        if (navigator.deviceMemory <= 2) memStatus = 'bad';
        else if (navigator.deviceMemory <= 4) memStatus = 'moderate';
        else memStatus = 'ok';
      }
      if (window.performance && window.performance.memory) {
        const { totalJSHeapSize, usedJSHeapSize, jsHeapSizeLimit } = window.performance.memory;
        memInfo.totalJSHeapSize = totalJSHeapSize;
        memInfo.usedJSHeapSize = usedJSHeapSize;
        memInfo.jsHeapSizeLimit = jsHeapSizeLimit;
        if (jsHeapSizeLimit && usedJSHeapSize / jsHeapSizeLimit > 0.8) {
          memStatus = 'bad';
        } else if (jsHeapSizeLimit && usedJSHeapSize / jsHeapSizeLimit > 0.6) {
          memStatus = memStatus === 'bad' ? 'bad' : 'moderate';
        }
      }
      setMemoryStatus(memStatus);
      setMemoryInfo(memInfo);
    }
  }, []);

  // Dismiss logic for network
  useEffect(() => {
    if ((networkStatus === 'bad' || networkStatus === 'moderate') && dismissedNetwork && !networkTimerRef.current) {
      networkTimerRef.current = setTimeout(() => {
        setDismissedNetwork(false);
        networkTimerRef.current = null;
      }, DISMISS_TIMEOUT);
    }
    if ((networkStatus !== 'bad' && networkStatus !== 'moderate') && networkTimerRef.current) {
      clearTimeout(networkTimerRef.current);
      networkTimerRef.current = null;
    }
    // Cleanup on unmount
    return () => {
      if (networkTimerRef.current) {
        clearTimeout(networkTimerRef.current);
        networkTimerRef.current = null;
      }
    };
  }, [networkStatus, dismissedNetwork]);

  // Dismiss logic for memory
  useEffect(() => {
    if ((memoryStatus === 'bad' || memoryStatus === 'moderate') && dismissedMemory && !memoryTimerRef.current) {
      memoryTimerRef.current = setTimeout(() => {
        setDismissedMemory(false);
        memoryTimerRef.current = null;
      }, DISMISS_TIMEOUT);
    }
    if ((memoryStatus !== 'bad' && memoryStatus !== 'moderate') && memoryTimerRef.current) {
      clearTimeout(memoryTimerRef.current);
      memoryTimerRef.current = null;
    }
    // Cleanup on unmount
    return () => {
      if (memoryTimerRef.current) {
        clearTimeout(memoryTimerRef.current);
        memoryTimerRef.current = null;
      }
    };
  }, [memoryStatus, dismissedMemory]);

  const handleCardClick = () => {
    setIsExpanded(!isExpanded);
  };

  // Dismiss handlers
  const handleDismissNetwork = (e) => {
    e.stopPropagation();
    setDismissedNetwork(true);
  };
  const handleDismissMemory = (e) => {
    e.stopPropagation();
    setDismissedMemory(true);
  };

  // Show offline card if offline
  if (!isOnline) {
    return (
      <div className="message-card message-error expanded">
        <h4>No Internet Connection</h4>
        <p>Your device is offline. Please check your internet connection.</p>
      </div>
    );
  }

  if (((networkStatus === null || networkStatus === 'ok') || dismissedNetwork) && ((memoryStatus === null || memoryStatus === 'ok') || dismissedMemory)) {
    return null;
  }

  return (
    <>
      {/* Network Card */}
      {(networkStatus === 'bad' || networkStatus === 'moderate') && !dismissedNetwork && (
        <div
          className={`message-card message-${networkStatus === 'bad' ? 'error' : 'warning'} ${isExpanded ? 'expanded' : 'collapsed'}`}
          onClick={handleCardClick}
        >
          <button
            className="message-card-dismiss"
            style={{ position: 'absolute', top: 10, right: 16, background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', zIndex: 2 }}
            aria-label="Dismiss network warning"
            onClick={handleDismissNetwork}
          >
            ×
          </button>
          <h4>{networkStatus === 'bad' ? 'Slow Internet Connection Detected' : 'Moderate Internet Connection'}</h4>
          {isExpanded && (
            <>
              <p>
                {networkStatus === 'bad'
                  ? 'Your current connection speed is very low and may affect app performance.'
                  : 'Your connection speed is moderate and may cause some slowdowns.'}
              </p>
              <p>Try switching to a stronger Wi-Fi or mobile data.</p>
              {measuredSpeed !== null && (
                <p>Measured speed: {measuredSpeed.toFixed(2)} Mbps</p>
              )}
            </>
          )}
        </div>
      )}
      {/* Memory Card */}
      {(memoryStatus === 'bad' || memoryStatus === 'moderate') && !dismissedMemory && (
        <div
          className={`message-card message-${memoryStatus === 'bad' ? 'error' : 'warning'} ${isExpanded ? 'expanded' : 'collapsed'}`}
          onClick={handleCardClick}
        >
          <button
            className="message-card-dismiss"
            style={{ position: 'absolute', top: 10, right: 16, background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', zIndex: 2 }}
            aria-label="Dismiss memory warning"
            onClick={handleDismissMemory}
          >
            ×
          </button>
          <h4>{memoryStatus === 'bad' ? 'High Memory Usage Detected' : 'Moderate Memory Usage'}</h4>
          {isExpanded && (
            <>
              <p>
                {memoryStatus === 'bad'
                  ? 'Your device is low on available memory or the browser is using a lot of memory. This may cause slowdowns or unresponsive features.'
                  : 'Your device memory is moderate or browser memory usage is getting high. Performance may be affected.'}
              </p>
              {memoryInfo.deviceMemory && (
                <p>Device memory: {memoryInfo.deviceMemory} GB</p>
              )}
              {memoryInfo.jsHeapSizeLimit && (
                <p>
                  JS Heap: {formatBytes(memoryInfo.usedJSHeapSize)} / {formatBytes(memoryInfo.totalJSHeapSize)} (Limit: {formatBytes(memoryInfo.jsHeapSizeLimit)})
                </p>
              )}
              <p>Try closing other tabs/apps to improve performance.</p>
            </>
          )}
        </div>
      )}
    </>
  );
}